import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, FileUp, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
  EmptyState,
  ErrorState,
  FieldLabel,
  LoadingState,
  Modal,
  PageHeading,
} from "@/components/app/AppUI";
import { eventRepository, fileRepository } from "@/data/repositories";
import { FILE_CATEGORY_LABELS } from "@/domain/songs";
import type { EventFile, FileCategory } from "@/domain/types";

export const Route = createFileRoute("/_authenticated/app/archivos")({ component: FilesPage });
function FilesPage() {
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const files = useQuery({ queryKey: ["files"], queryFn: () => fileRepository.list() });
  const events = useQuery({ queryKey: ["events"], queryFn: () => eventRepository.list() });
  const upload = useMutation({
    mutationFn: (input: {
      eventId: string;
      file: File;
      category: FileCategory;
      visible: boolean;
    }) => fileRepository.upload(input.eventId, input.file, input.category, input.visible),
    onSuccess: () => {
      toast.success("Archivo subido");
      setOpen(false);
      client.invalidateQueries({ queryKey: ["files"] });
    },
    onError: (error) => toast.error(error.message),
  });
  if (files.isLoading || events.isLoading) return <LoadingState />;
  if (files.error || events.error)
    return <ErrorState message={files.error?.message ?? events.error?.message ?? "Error"} />;
  async function download(file: EventFile) {
    try {
      window.open(await fileRepository.signedUrl(file), "_blank", "noopener,noreferrer");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No se pudo descargar");
    }
  }
  return (
    <div className="space-y-7">
      <PageHeading
        eyebrow="Documentación"
        title="Archivos"
        description="Documentos, imágenes y audio privados con enlaces temporales."
        action={
          <button className="btn-primary" onClick={() => setOpen(true)}>
            <FileUp className="size-4" /> Subir archivo
          </button>
        }
      />
      <section className="panel">
        {(files.data ?? []).length === 0 ? (
          <EmptyState
            title="Sin archivos"
            description="Sube contratos, música, riders o documentación."
          />
        ) : (
          files.data?.map((file) => (
            <div
              key={file.id}
              className="data-row grid-cols-[1fr_auto] md:grid-cols-[1.2fr_1fr_8rem_8rem_auto]"
            >
              <div>
                <p className="font-semibold">{file.original_filename}</p>
                <p className="text-sm text-muted-foreground">{file.content_type}</p>
              </div>
              <p className="hidden md:block">{file.events?.title ?? "Evento"}</p>
              <p className="hidden text-sm md:block">{FILE_CATEGORY_LABELS[file.category]}</p>
              <p className="hidden text-sm md:block">
                {(file.size_bytes / 1024 / 1024).toFixed(1)} MB
              </p>
              <div className="flex gap-2">
                <button
                  className="btn-quiet !min-h-9 !px-3"
                  onClick={() => download(file)}
                  aria-label="Descargar"
                >
                  <Download className="size-4" />
                </button>
                <button
                  className="btn-danger !min-h-9 !px-3"
                  onClick={async () => {
                    if (!confirm("¿Eliminar archivo?")) return;
                    await fileRepository.remove(file);
                    toast.success("Archivo eliminado");
                    client.invalidateQueries({ queryKey: ["files"] });
                  }}
                  aria-label="Eliminar"
                >
                  <Trash2 className="size-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </section>
      {open && (
        <UploadModal
          events={events.data ?? []}
          saving={upload.isPending}
          onClose={() => setOpen(false)}
          onUpload={(input) => upload.mutate(input)}
        />
      )}
    </div>
  );
}
function UploadModal({
  events,
  saving,
  onClose,
  onUpload,
}: {
  events: Awaited<ReturnType<typeof eventRepository.list>>;
  saving: boolean;
  onClose: () => void;
  onUpload: (input: {
    eventId: string;
    file: File;
    category: FileCategory;
    visible: boolean;
  }) => void;
}) {
  const [eventId, setEventId] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [category, setCategory] = useState<FileCategory>("DOCUMENT");
  const [visible, setVisible] = useState(true);
  return (
    <Modal title="Subir archivo" onClose={onClose}>
      <form
        className="space-y-5"
        onSubmit={(event) => {
          event.preventDefault();
          if (eventId && file) onUpload({ eventId, file, category, visible });
        }}
      >
        <FieldLabel label="Evento*">
          <select
            className="field"
            required
            value={eventId}
            onChange={(event) => setEventId(event.target.value)}
          >
            <option value="">Selecciona</option>
            {events.map((item) => (
              <option key={item.id} value={item.id}>
                {item.title}
              </option>
            ))}
          </select>
        </FieldLabel>
        <FieldLabel label="Archivo*">
          <input
            className="field"
            type="file"
            required
            accept=".pdf,.docx,.jpg,.jpeg,.png,.webp,.mp3,.wav,.m4a"
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          />
        </FieldLabel>
        <FieldLabel label="Categoría">
          <select
            className="field"
            value={category}
            onChange={(event) => setCategory(event.target.value as FileCategory)}
          >
            <option value="CONTRACT">Contrato</option>
            <option value="QUOTE">Presupuesto</option>
            <option value="MUSIC">Música</option>
            <option value="IMAGE">Imagen</option>
            <option value="DOCUMENT">Documento</option>
            <option value="RIDER">Rider</option>
            <option value="OTHER">Otro</option>
          </select>
        </FieldLabel>
        <label className="flex items-center gap-3 border border-border p-4">
          <input
            type="checkbox"
            checked={visible}
            onChange={(event) => setVisible(event.target.checked)}
          />
          <span>
            <strong className="block">Visible para el cliente</strong>
            <span className="text-sm text-muted-foreground">
              Podrá descargarlo desde su portal.
            </span>
          </span>
        </label>
        <div className="flex justify-end gap-3 border-t border-border pt-5">
          <button type="button" className="btn-quiet" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn-primary" disabled={saving || !file || !eventId}>
            {saving ? "Subiendo…" : "Subir"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
